import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { 
  CreditCard, 
  ExternalLink, 
  ShieldCheck, 
  AlertCircle,
  DollarSign,
  TrendingUp,
  Percent,
  Wallet,
  Zap
} from 'lucide-react';
import { 
  getStripeConnectLink,
  getStripeDashboardLink
} from '../../api/worker';
import { usePermissions } from '../../hooks/usePermissions';
import { useUpdateNodeConfig } from '../../hooks/useNode';
import { useReferrals } from '../../hooks/useReferrals';
import toast from 'react-hot-toast';

export const PayoutSettingsPage = () => {
  const navigate = useNavigate();
  const { node, brand } = useTenant();
  const { can, isAdmin } = usePermissions();
  const canManageFinancials = isAdmin || can('CONFIG_UPDATE') || can('manage_settings') || can('manage_billing');

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [requiredDues, setRequiredDues] = useState<string>('0');
  const [isSaving, setIsSaving] = useState(false);

  const handleViewDashboard = async () => {
    if (!node?.id) return;
    try {
      setIsLoadingDashboard(true);
      const data = await getStripeDashboardLink(node.id);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Failed to load Stripe dashboard');
      }
    } catch (error) {
      toast.error('Failed to load Stripe dashboard');
    } finally {
      setIsLoadingDashboard(false);
    }
  };

  const getPlatformFee = (tier?: string) => {
    switch (tier) {
      case 'MOMENTUM': return 0;
      case 'FOUNDATION': return 2;
      case 'LOCAL': default: return 5;
    }
  };
  
  const platformFee = getPlatformFee(node?.tier);
  const upSplit = 3;
  const retainedPortion = 100 - platformFee - upSplit;

  const updateConfig = useUpdateNodeConfig(node?.id || '');
  const { data: referrals = [] } = useReferrals(node?.id || '');
  const totalCredits = referrals.reduce((acc, r) => acc + (r.creditsEarned || 0), 0) / 100;

  const [prevPayoutNode, setPrevPayoutNode] = useState<typeof node | null>(null);
  if (node && prevPayoutNode !== node) {
    setPrevPayoutNode(node);
    if (node?.config?.requiredDues) {
      setRequiredDues(String(node?.config?.requiredDues || '0'));
    }
  }

  const handleSaveFinancials = async () => {
    if (!node?.id) return;
    setIsSaving(true);
    try {
      await updateConfig.mutateAsync({
        requiredDues: parseFloat(requiredDues) || 0
      });
      toast.success('Financial configuration updated');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const isConnected = !!(node as any)?.stripeAccountId || !!(node as any)?.config?.stripeAccountId;

  const handleConnectStripe = async () => {
    if (!node?.id) return;
    
    setIsLoading(true);
    try {
      const response = await getStripeConnectLink(node.id);
      if (response?.url) {
        window.location.href = response.url;
      } else {
        toast.error('Failed to retrieve connection link.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to get Stripe link', error);
      toast.error('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          Back
        </Button>
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Payment Processing</h1>
          <p className="text-xs md:text-sm text-gray-500">Connect your bank account to receive payouts securely.</p>
        </div>
      </div>

      <Card className="space-y-6">
        <div className="flex items-center justify-between border-b border-black dark:border-white pb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 text-blue-700 rounded-none">
              <CreditCard size={24} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Bank Account & Payouts</h3>
              <p className="text-sm text-gray-500">Route payments directly to your node's bank account.</p>
            </div>
          </div>
          {isConnected && (
            <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-none text-sm font-bold border-2 border-black dark:border-white border-green-100">
              <ShieldCheck size={16} />
              Stripe Account Connected
            </div>
          )}
        </div>

        {isConnected ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 leading-relaxed">
              Your organization is fully connected to Stripe. You can now accept member dues, receive sponsorships, and process event registrations securely. Platform fees are automatically handled during checkout.
            </p>
            <div className="pt-2">
              <Button 
                variant="secondary" 
                className="gap-2" 
                onClick={handleViewDashboard}
                disabled={isLoadingDashboard}
              >
                {isLoadingDashboard ? 'Opening...' : 'View Stripe Dashboard'}
                <ExternalLink size={16} />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {node?.tier === 'LOCAL' && (
              <div className="p-4 bg-indigo-50 border-2 border-black dark:border-white border-indigo-100 rounded-none flex gap-3 text-indigo-900 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-indigo-950/30 dark:border-indigo-900/50 dark:text-indigo-200">
                <Zap size={20} className="shrink-0 text-indigo-600 dark:text-indigo-400 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="text-sm font-bold">Want to skip the complex Stripe setup?</h4>
                  <p className="text-sm leading-relaxed opacity-90">
                    Upgrade to the Foundation tier for seamless, 2-minute payment onboarding and instant payouts.
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-indigo-700 hover:bg-indigo-100 font-bold px-0 dark:text-indigo-300 dark:hover:bg-indigo-900/50"
                    onClick={() => navigate('/settings/billing')}
                  >
                    View Upgrade Plans →
                  </Button>
                </div>
              </div>
            )}
          
            <div className="p-4 bg-amber-50 border-2 border-black dark:border-white border-amber-100 rounded-none flex gap-3 text-amber-800">
              <AlertCircle size={20} className="shrink-0" />
              <p className="text-sm leading-relaxed">
                Connect your bank account to start accepting dues and sponsorships. 
                {brand?.name} uses Stripe to securely process payments and route funds directly to your organization.
              </p>
            </div>
            
            <div className="pt-2">
              {canManageFinancials ? (
                <Button 
                  onClick={handleConnectStripe} 
                  isLoading={isLoading}
                  className="bg-[#635BFF] hover:bg-[#5851E5] text-white border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-[#635BFF]/20"
                >
                  Connect with Stripe
                </Button>
              ) : (
                <p className="text-xs text-gray-500 italic">Stripe connection requires CONFIG_UPDATE permission.</p>
              )}
            </div>
          </div>
        )}
      </Card>

      <Card className="space-y-6">
        <div className="flex items-center gap-3 border-b border-black dark:border-white pb-4">
          <div className="p-3 bg-green-50 text-green-700 rounded-none">
            <DollarSign size={24} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Member Dues Configuration</h3>
            <p className="text-sm text-gray-500">Define the expected contribution amount for members of this node.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Annual Required Dues ($)</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-xs">$</span>
                </div>
                <Input 
                  type="number"
                  value={requiredDues}
                  onChange={(e) => setRequiredDues(e.target.value)}
                  className="pl-7"
                  placeholder="0.00"
                  disabled={!canManageFinancials}
                />
              </div>
              <p className="mt-2 text-[10px] text-gray-500">
                This amount is used to calculate the "Contribution Progress" in the member's ledger view.
              </p>
            </div>

            {canManageFinancials && (
              <Button 
                onClick={handleSaveFinancials}
                isLoading={isSaving}
                className="bg-navy-900 text-white rounded-none font-bold px-8 h-10 text-xs shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-navy-900/10"
              >
                Update Ledger Target
              </Button>
            )}
          </div>

          <div className="bg-white rounded-none p-6 border-2 border-black dark:border-white dark:bg-slate-950">
            <h4 className="text-xs font-bold text-navy-900 uppercase tracking-widest mb-4 flex items-center gap-2">
              <TrendingUp size={14} className="text-green-600" />
              Org Split Policy
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Org Retained Portion</span>
                <span className="text-navy-900 font-black">{retainedPortion}%</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Sponsorship Up-Split</span>
                <span className="text-navy-900 font-black">{upSplit}%</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Platform Service Fee</span>
                <span className="text-navy-900 font-black">{platformFee}%</span>
              </div>
              <div className="h-px bg-gray-200 my-2" />
              <div className="flex justify-between items-center text-xs">
                <span className="text-navy-900 font-bold">Total Payout Efficiency</span>
                <span className="text-green-600 font-black italic">{retainedPortion.toFixed(1)}%</span>
              </div>
            </div>
            {platformFee > 0 && (
              <Button 
                variant="secondary" 
                className="w-full mt-4 text-[10px] font-bold uppercase tracking-widest text-brand-primary border-brand-primary/20 hover:bg-brand-primary/5"
                onClick={() => navigate('/settings/billing')}
              >
                Upgrade to lower your fee
              </Button>
            )}
            <div className="mt-6 flex items-start gap-3 p-3 bg-white rounded-none border-2 border-black dark:border-white dark:bg-slate-900">
              <Percent size={14} className="text-blue-600 mt-0.5" />
              <p className="text-[10px] text-gray-500 leading-relaxed">
                Fees are calculated automatically at checkout. Splits are routed via Stripe Connect based on child-parent node relationships.
              </p>
            </div>
          </div>
        </div>
      </Card>
      
      <Card className="bg-gradient-to-br from-navy-900 to-navy-800 text-white border-none shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Wallet size={120} />
        </div>
        
        <div className="relative z-10 space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/10 rounded-none dark:bg-slate-900">
              <ShieldCheck size={24} className="text-green-400" />
            </div>
            <div>
              <h3 className="font-bold text-white tracking-tight">Manual Adjustments</h3>
              <p className="text-sm text-blue-100/60">Manage manual ledger entries and audits.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-white/5 rounded-none border-2 border-black dark:border-white border-white/10 flex flex-col justify-between dark:bg-slate-900">
              <div>
                <h5 className="text-xs font-bold uppercase tracking-wider text-blue-200/80 mb-2">Ledger Integrity</h5>
                <p className="text-[11px] text-blue-100/60 leading-relaxed">Ensure all offline payments are recorded in the system ledger to maintain accurate member standings.</p>
              </div>
              <Button variant="ghost" className="mt-4 text-white hover:bg-white/10 justify-start h-8 text-[10px] font-bold uppercase tracking-widest pl-0 dark:bg-slate-900" onClick={() => navigate('/ledger')}>
                Go to Ledger Studio <ExternalLink size={12} className="ml-2" />
              </Button>
            </div>
            <div className="p-4 bg-white/5 rounded-none border-2 border-black dark:border-white border-white/10 flex flex-col justify-between dark:bg-slate-900">
              <div>
                <h5 className="text-xs font-bold uppercase tracking-wider text-blue-200/80 mb-2">Platform Credits</h5>
                <p className="text-[11px] text-blue-100/60 leading-relaxed">Referral credits and platform rewards are applied directly to your organization's balance twice monthly.</p>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-[10px] font-bold text-blue-200/50">Current Balance</span>
                <span className="text-lg font-black text-white italic tracking-tighter">${totalCredits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
